import { useId, useState, type ReactNode } from 'react';
import type { AforoEvento } from '../../api/ops/eventos';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { comprometidas, tramosAforo } from './reglas';
import '../../componentes/campos.css';

/**
 * Piezas propias de «Eventos»: el campo de día y hora (la píldora de RV Fecha con `datetime-local`, en la hora de la zona
 * del evento), la barra de aforo apilada con su leyenda y «Ver como tabla» (G290), las cifras en tarjeta y las líneas de
 * impacto con icono.
 */
export function CampoDiaHora({ etiqueta, valor, onChange, error, ayuda, bloqueado = false }: { etiqueta: string; valor: string; onChange: (v: string) => void; error?: string | null; ayuda?: string | null; bloqueado?: boolean }) {
    const id = useId();
    const [foco, setFoco] = useState(false);
    const hayError = error != null && error !== '';
    const hayAyuda = !hayError && ayuda != null && ayuda !== '';
    const caja = ['rv-campo__caja', 'rv-campo__caja--fecha', bloqueado ? 'rv-campo__caja--bloqueado' : '', foco ? 'rv-campo__caja--foco' : '', hayError ? 'rv-campo__caja--error' : ''].filter((c) => c !== '').join(' ');
    return (
        <div className="rv-campo">
            <div className="rv-campo__fila">
                <label htmlFor={id} className={caja}>
                    <Icono nombre="calendar" className="rv-campo__calendario" />
                    <span className="rv-campo__etiqueta rv-campo__etiqueta--arriba">{etiqueta}</span>
                    <input
                        id={id}
                        type="datetime-local"
                        value={valor}
                        disabled={bloqueado}
                        aria-invalid={hayError}
                        aria-describedby={hayError || hayAyuda ? `${id}-nota` : undefined}
                        onChange={(e) => { onChange(e.target.value); }}
                        onFocus={() => { setFoco(true); }}
                        onBlur={() => { setFoco(false); }}
                    />
                </label>
            </div>
            {hayError && (
                <span id={`${id}-nota`} role="alert" className="rv-campo__error">
                    <Icono nombre="circle-alert" />
                    <span>{error}</span>
                </span>
            )}
            {hayAyuda && <span id={`${id}-nota`} className="rv-campo__ayuda">{ayuda}</span>}
        </div>
    );
}

/** Barra apilada del aforo (vendidas, pendientes, invitados, cupo de taquilla y libres), como en Informes 10.8. */
export function BarraAforo({ aforo, fina = false }: { aforo: AforoEvento; fina?: boolean }) {
    const total = Math.max(1, aforo.capacity);
    return (
        <span className={`rv-ev-barra${fina ? ' rv-ev-barra--fina' : ''}`} aria-hidden="true">
            {tramosAforo(aforo).filter((t) => t.etiqueta !== 'Libres' && t.plazas > 0).map((t) => (
                <span key={t.etiqueta} style={{ width: `${String(Math.min(100, (t.plazas / total) * 100))}%`, background: t.color }} />
            ))}
        </span>
    );
}

export function TarjetaAforo({ aforo, titulo, invitados }: { aforo: AforoEvento; titulo: string; invitados?: number | null }) {
    const [tabla, setTabla] = useState(false);
    const usadas = comprometidas(aforo);
    const tramos = tramosAforo(aforo);
    return (
        <section className="rv-ev-aforo" aria-label={titulo}>
            <strong className="rv-ev-aforo__titulo">{titulo}</strong>
            <span className="rv-ev-aforo__cifra">
                <b>{usadas}</b> de {aforo.capacity} plazas
            </span>
            <span className="rv-ev-aforo__nota">{aforo.available > 0 ? `Comprometidas · quedan ${String(aforo.available)} libres` : 'Aforo completo'}</span>
            <BarraAforo aforo={aforo} />
            {tabla ? (
                <table className="rv-ev-tabla">
                    <tbody>
                        {tramos.map((t) => (
                            <tr key={t.etiqueta}>
                                <th scope="row">{t.etiqueta}</th>
                                <td>{t.plazas} plazas</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <ul className="rv-ev-leyenda">
                    {tramos.map((t) => (
                        <li key={t.etiqueta}>
                            <span className="rv-ev-leyenda__punto" style={{ background: t.color }} />
                            {t.etiqueta} <b>{t.plazas} plazas</b>
                        </li>
                    ))}
                </ul>
            )}
            {invitados != null && <span className="rv-ev-aforo__nota">{`De las listas: ${String(invitados)} invitados con sus acompañantes.`}</span>}
            <button type="button" className="rv-ev-enlace" onClick={() => { setTabla((v) => !v); }}>
                <Icono nombre={tabla ? 'gauge' : 'table'} />
                <span>{tabla ? 'Ver como barra' : 'Ver como tabla'}</span>
            </button>
        </section>
    );
}

export function Cifras({ items }: { items: readonly { titulo: string; cifra: string; unidad?: string; nota?: string; oscura?: boolean }[] }) {
    return (
        <div className="rv-ev-cifras">
            {items.map((c) => (
                <div key={c.titulo} className={`rv-ev-cifra${c.oscura === true ? ' rv-ev-cifra--oscura' : ''}`}>
                    <span className="rv-ev-cifra__titulo">{c.titulo}</span>
                    <span className="rv-ev-cifra__valor">
                        {c.cifra}
                        {c.unidad !== undefined && <small> {c.unidad}</small>}
                    </span>
                    {c.nota !== undefined && <span className="rv-ev-cifra__nota">{c.nota}</span>}
                </div>
            ))}
        </div>
    );
}

export function Lineas({ items }: { items: readonly { icono: NombreIcono; titulo: string; texto: ReactNode }[] }) {
    return (
        <ul className="rv-ev-lineas">
            {items.map((l) => (
                <li key={l.titulo}>
                    <Icono nombre={l.icono} />
                    <span>
                        <strong>{l.titulo}</strong>
                        <span>{l.texto}</span>
                    </span>
                </li>
            ))}
        </ul>
    );
}

export function Aviso({ tono, icono, titulo, children, accion }: { tono: 'negro' | 'amarillo' | 'rojo' | 'gris'; icono: NombreIcono; titulo?: string; children?: ReactNode; accion?: ReactNode }) {
    return (
        <div className={`rv-ev-aviso rv-ev-aviso--${tono}`} role={tono === 'rojo' ? 'alert' : 'status'}>
            <Icono nombre={icono} />
            <span className="rv-ev-aviso__texto">
                {titulo !== undefined && <strong>{titulo}</strong>}
                {children !== undefined && <span>{children}</span>}
            </span>
            {accion}
        </div>
    );
}

export function Ceja({ children }: { children: ReactNode }) {
    return <p className="rv-ev-ceja">{children}</p>;
}
